/**
 * Единая модель бейджа строки ленты FILEATTACHMENTS (WUI-293 / WUI-324 / WUI-344).
 * Приоритет: R7 busy → R7 error → R7 staged → platform pending → platform synced (idle).
 *
 * @see dev/contracts/attachments.md — «Матрица бейджей ленты»
 */
import type { FileItem } from '@/common/types/fileItem'
import type { DsAttachmentPhase } from '@/common/utils/dsAttachmentPhases'
import {
  isDsAttachmentBusyPhase,
  isDsAttachmentOpenBlockedPhase,
} from '@/common/utils/dsAttachmentPhases'
import {
  type FileAttachmentsElementMeta,
  isAttachmentRefillWireRowAwaitingFile,
  isAttachmentRowPendingPlatformUpload,
} from '@/common/utils/attachmentRpcPolicy'
import { isNewLocalAttachmentItem, rowHasServerCode } from '@/common/utils/fileAttachmentsUniData'

export type AttachmentTapeBadgeKind =
  | 'none'
  | 'r7_editing'
  | 'r7_downloading'
  | 'r7_awaiting_ws'
  | 'r7_staged'
  | 'r7_error'
  | 'platform_pending'
  | 'platform_refill_pending'
  | 'platform_synced'

export type AttachmentTapeBadgeUi = {
  kind: AttachmentTapeBadgeKind
  visible: boolean
  busy: boolean
  clickBlocked: boolean
  cssClass: string
  iconClass: string
  title: string
  /** Фаза DS/R7, если бейдж относится к Office-циклу. */
  phase: DsAttachmentPhase | null
}

const EMPTY_UI: AttachmentTapeBadgeUi = {
  kind: 'none',
  visible: false,
  busy: false,
  clickBlocked: false,
  cssClass: '',
  iconClass: '',
  title: '',
  phase: null,
}

export type ResolveAttachmentTapeRowBadgeInput = {
  item: FileItem
  meta: FileAttachmentsElementMeta
  elementId?: number | null
  resolvePhase?: (item: FileItem) => DsAttachmentPhase | null
}

function buildR7Badge(phase: DsAttachmentPhase): AttachmentTapeBadgeUi {
  const kind: AttachmentTapeBadgeKind =
    phase === 'editing'
      ? 'r7_editing'
      : phase === 'downloading'
        ? 'r7_downloading'
        : phase === 'awaiting_ws'
          ? 'r7_awaiting_ws'
          : phase === 'staged'
            ? 'r7_staged'
            : 'r7_error'

  const titleByPhase: Partial<Record<DsAttachmentPhase, string>> = {
    awaiting_ws: 'Ожидание сохранения',
    downloading: 'Загрузка с сервера документов',
    editing: 'Редактирование в Р7',
    staged: 'Сохранён на сервер документов, ожидает сохранения формы',
    error: 'Ошибка синхронизации',
    discarded: 'Ошибка синхронизации',
  }

  const iconClass =
    phase === 'staged'
      ? 'pi pi-cloud-upload'
      : phase === 'editing'
        ? 'pi pi-pencil'
        : isDsAttachmentBusyPhase(phase)
          ? 'pi pi-sync'
          : 'pi pi-exclamation-triangle'

  return {
    kind,
    visible: true,
    busy: isDsAttachmentBusyPhase(phase),
    clickBlocked: isDsAttachmentOpenBlockedPhase(phase),
    cssClass: `attachment-tape-badge--${phase === 'staged' ? 'staged' : phase}`,
    iconClass,
    title: titleByPhase[phase] ?? 'Ошибка синхронизации',
    phase,
  }
}

function buildPlatformPendingBadge(refill: boolean): AttachmentTapeBadgeUi {
  return {
    kind: refill ? 'platform_refill_pending' : 'platform_pending',
    visible: true,
    busy: false,
    clickBlocked: false,
    cssClass: 'attachment-tape-badge--platform-pending',
    iconClass: 'pi pi-cloud-upload',
    title: refill
      ? 'Ожидает прикрепления файла, затем загрузка при сохранении формы'
      : 'Будет загружен при сохранении формы',
    phase: null,
  }
}

function buildPlatformSyncedBadge(): AttachmentTapeBadgeUi {
  return {
    kind: 'platform_synced',
    visible: true,
    busy: false,
    clickBlocked: false,
    cssClass: 'attachment-tape-badge--idle',
    iconClass: 'pi pi-check',
    title: 'Файл на платформе',
    phase: 'idle',
  }
}

/** DS `idle` на новом локальном Office не считаем «синхронизирован» — это platform pending. */
function isActionableR7Phase(
  phase: DsAttachmentPhase,
  item: FileItem,
): boolean {
  if (phase === 'idle' && isNewLocalAttachmentItem(item)) return false
  if (phase === 'none') return false
  if (phase === 'idle') return false
  return true
}

/**
 * Один бейдж на карточку ленты по приоритету состояний строки.
 */
export function resolveAttachmentTapeRowBadge(input: ResolveAttachmentTapeRowBadgeInput): AttachmentTapeBadgeUi {
  const { item, meta, elementId, resolvePhase } = input
  const phase = resolvePhase?.(item) ?? null

  if (phase && isActionableR7Phase(phase, item)) {
    if (
      phase === 'editing'
      || phase === 'staged'
      || isDsAttachmentBusyPhase(phase)
      || phase === 'error'
      || phase === 'discarded'
    ) {
      return buildR7Badge(phase)
    }
  }

  if (isAttachmentRowPendingPlatformUpload(item, meta, elementId)) {
    return buildPlatformPendingBadge(isAttachmentRefillWireRowAwaitingFile(item))
  }

  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) {
    return buildPlatformSyncedBadge()
  }

  return EMPTY_UI
}

/** @deprecated Используйте `AttachmentTapeBadgeUi`. */
export type AttachmentTapeSyncUi = AttachmentTapeBadgeUi
